import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Person,
  CalendarToday,
  AccessTime,
  Warning,
} from '@mui/icons-material';
import api from '../services/api';

const STAFF_ROLES = {
  lead: { label: 'Lead', color: '#1976d2' },
  assistant: { label: 'Assistant', color: '#2e7d32' },
  cashier: { label: 'Cashier', color: '#ed6c02' },
  security: { label: 'Security', color: '#d32f2f' },
  setup: { label: 'Setup Crew', color: '#9c27b0' },
};

const StaffScheduler = ({ saleId }) => {
  const [loading, setLoading] = useState(true);
  const [saleDays, setSaleDays] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [conflicts, setConflicts] = useState([]);

  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Assignment form state
  const [assignmentForm, setAssignmentForm] = useState({
    staff_id: '',
    role: 'assistant',
    start_time: '09:00',
    end_time: '17:00',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, [saleId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchSaleDays(),
        fetchStaffMembers(),
        fetchAssignments(),
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Failed to load scheduling data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSaleDays = async () => {
    try {
      const response = await api.get(`/sales/${saleId}/days`);
      setSaleDays(response.data?.days || response.data || []);
    } catch (error) {
      console.error('Error fetching sale days:', error);
      throw error;
    }
  };

  const fetchStaffMembers = async () => {
    try {
      const response = await api.get('/staff');
      setStaffMembers(response.data?.staff || response.data || []);
    } catch (error) {
      console.error('Error fetching staff:', error);
      throw error;
    }
  };

  const fetchAssignments = async () => {
    try {
      const response = await api.get(`/sales/${saleId}/staff`);
      const assignmentsData = response.data?.assignments || response.data || [];
      setAssignments(assignmentsData);
      detectConflicts(assignmentsData);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      throw error;
    }
  };

  const detectConflicts = (assignmentsData) => {
    const conflictList = [];
    const staffSchedule = {};

    assignmentsData.forEach(assignment => {
      const key = `${assignment.staff_id}-${assignment.sale_day_id}`;
      if (staffSchedule[key]) {
        conflictList.push({
          staff_id: assignment.staff_id,
          day_id: assignment.sale_day_id,
          message: 'Staff member has multiple assignments on this day',
        });
      }
      staffSchedule[key] = true;
    });

    setConflicts(conflictList);
  };

  const getAssignmentsForDay = (dayId) => {
    return assignments.filter(a => a.sale_day_id === dayId);
  };

  const getStaffName = (staffId) => {
    const staff = staffMembers.find(s => s.id === staffId);
    return staff ? `${staff.first_name} ${staff.last_name}` : 'Unknown';
  };

  const hasConflict = (staffId, dayId) => {
    return conflicts.some(c => c.staff_id === staffId && c.day_id === dayId);
  };

  const handleOpenAssignDialog = (day) => {
    setSelectedDay(day);
    setAssignmentForm({
      staff_id: '',
      role: 'assistant',
      start_time: day.start_time || '09:00',
      end_time: day.end_time || '17:00',
      notes: '',
    });
    setOpenAssignDialog(true);
  };

  const handleOpenEditDialog = (assignment) => {
    setSelectedAssignment(assignment);
    setAssignmentForm({
      staff_id: assignment.staff_id,
      role: assignment.role,
      start_time: assignment.start_time,
      end_time: assignment.end_time,
      notes: assignment.notes || '',
    });
    setOpenEditDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenAssignDialog(false);
    setOpenEditDialog(false);
    setSelectedDay(null);
    setSelectedAssignment(null);
    setAssignmentForm({
      staff_id: '',
      role: 'assistant',
      start_time: '09:00',
      end_time: '17:00',
      notes: '',
    });
  };

  const validateForm = () => {
    if (!assignmentForm.staff_id) {
      showSnackbar('Please select a staff member', 'error');
      return false;
    }
    if (assignmentForm.end_time <= assignmentForm.start_time) {
      showSnackbar('End time must be after start time', 'error');
      return false;
    }
    return true;
  };

  const handleAssignStaff = async () => {
    if (!validateForm()) return;

    try {
      const data = {
        ...assignmentForm,
        sale_day_id: selectedDay.id,
      };

      await api.post(`/sales/${saleId}/staff`, data);
      showSnackbar('Staff assigned successfully', 'success');
      handleCloseDialogs();
      fetchAssignments();
    } catch (error) {
      console.error('Error assigning staff:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Failed to assign staff';
      showSnackbar(errorMsg, 'error');
    }
  };

  const handleUpdateAssignment = async () => {
    if (!validateForm()) return;

    try {
      await api.put(`/sales/${saleId}/staff/${selectedAssignment.id}`, assignmentForm);
      showSnackbar('Assignment updated successfully', 'success');
      handleCloseDialogs();
      fetchAssignments();
    } catch (error) {
      console.error('Error updating assignment:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Failed to update assignment';
      showSnackbar(errorMsg, 'error');
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to remove this staff assignment?')) return;

    try {
      await api.delete(`/sales/${saleId}/staff/${assignmentId}`);
      showSnackbar('Assignment removed successfully', 'success');
      fetchAssignments();
    } catch (error) {
      console.error('Error deleting assignment:', error);
      showSnackbar('Failed to remove assignment', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleFormChange = (field, value) => {
    setAssignmentForm({ ...assignmentForm, [field]: value });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {conflicts.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold">
            Scheduling Conflicts Detected
          </Typography>
          <Typography variant="caption">
            {conflicts.length} staff member(s) have conflicting assignments
          </Typography>
        </Alert>
      )}

      {saleDays.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CalendarToday sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No sale days scheduled
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add sale days before assigning staff
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Assigned Staff</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {saleDays.map((day) => {
                const dayAssignments = getAssignmentsForDay(day.id);
                return (
                  <TableRow key={day.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarToday fontSize="small" color="action" />
                        <Typography variant="body2">
                          {new Date(day.sale_date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <AccessTime fontSize="small" color="action" />
                        <Typography variant="body2">
                          {day.start_time} - {day.end_time}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {dayAssignments.length > 0 ? (
                        <Box display="flex" flexDirection="column" gap={1}>
                          {dayAssignments.map((assignment) => (
                            <Box key={assignment.id} display="flex" alignItems="center" gap={1}>
                              <Chip
                                icon={<Person />}
                                label={getStaffName(assignment.staff_id)}
                                size="small"
                                sx={{
                                  bgcolor: STAFF_ROLES[assignment.role]?.color || '#757575',
                                  color: 'white',
                                }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {STAFF_ROLES[assignment.role]?.label || assignment.role}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ({assignment.start_time} - {assignment.end_time})
                              </Typography>
                              {hasConflict(assignment.staff_id, day.id) && (
                                <Tooltip title="Scheduling conflict">
                                  <Warning fontSize="small" color="warning" />
                                </Tooltip>
                              )}
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditDialog(assignment)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteAssignment(assignment.id)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No staff assigned
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Add />}
                        onClick={() => handleOpenAssignDialog(day)}
                      >
                        Assign Staff
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Assign Staff Dialog */}
      <Dialog open={openAssignDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assign Staff to {selectedDay && new Date(selectedDay.sale_date).toLocaleDateString()}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Staff Member</InputLabel>
                  <Select
                    value={assignmentForm.staff_id}
                    label="Staff Member"
                    onChange={(e) => handleFormChange('staff_id', e.target.value)}
                  >
                    {staffMembers.map((staff) => (
                      <MenuItem key={staff.id} value={staff.id}>
                        {staff.first_name} {staff.last_name} - {staff.position || 'Staff'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={assignmentForm.role}
                    label="Role"
                    onChange={(e) => handleFormChange('role', e.target.value)}
                  >
                    {Object.entries(STAFF_ROLES).map(([key, config]) => (
                      <MenuItem key={key} value={key}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: config.color,
                            }}
                          />
                          {config.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="Start Time"
                  value={assignmentForm.start_time}
                  onChange={(e) => handleFormChange('start_time', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="End Time"
                  value={assignmentForm.end_time}
                  onChange={(e) => handleFormChange('end_time', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={assignmentForm.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  multiline
                  rows={2}
                  placeholder="Special instructions or notes..."
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Cancel</Button>
          <Button onClick={handleAssignStaff} variant="contained">
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Assignment Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Staff Assignment</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Staff Member</InputLabel>
                  <Select
                    value={assignmentForm.staff_id}
                    label="Staff Member"
                    onChange={(e) => handleFormChange('staff_id', e.target.value)}
                  >
                    {staffMembers.map((staff) => (
                      <MenuItem key={staff.id} value={staff.id}>
                        {staff.first_name} {staff.last_name} - {staff.position || 'Staff'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={assignmentForm.role}
                    label="Role"
                    onChange={(e) => handleFormChange('role', e.target.value)}
                  >
                    {Object.entries(STAFF_ROLES).map(([key, config]) => (
                      <MenuItem key={key} value={key}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: config.color,
                            }}
                          />
                          {config.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="Start Time"
                  value={assignmentForm.start_time}
                  onChange={(e) => handleFormChange('start_time', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="End Time"
                  value={assignmentForm.end_time}
                  onChange={(e) => handleFormChange('end_time', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={assignmentForm.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  multiline
                  rows={2}
                  placeholder="Special instructions or notes..."
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Cancel</Button>
          <Button onClick={handleUpdateAssignment} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StaffScheduler;
